<template>
  <LoadingIndicator v-if="!loaded" />
  <FormPage
    v-else-if="!showNotes"
    :lang-selector="isSettingEn"
    :loading="busy"
    :show-back="showBack"
    @back="onBack"
    @changeLang="event('daicho_form_01')"
  >
    <div v-if="edit.error" class="error">{{ edit.error }}</div>
    <div class="form">
      <FormGroup v-if="gnaviCourse" :title="$t('reserve/course')">
        <GnaviCourseInfo :course="gnaviCourse" small />
      </FormGroup>
      <hr v-if="gnaviCourse" />
      <FormGroup :title="isTakeOut ? $t('reserve/receiptDate') : $t('reserve/date')">
        <ReserveDateInput v-model="date" :open="!date" />
      </FormGroup>
      <FormGroup v-if="!isTakeOut" :title="$t('reserve/number')">
        <div v-if="invalidNumber && lastTouchKey" class="attention">{{ $t('invalid/number') }}</div>
        <ButtonSelect v-model="number" scroll :items="availableSeats" :disabled-values="disabledSeats">
          <template #default="{ value }">{{ $tc('reserve/number/count', value) }}</template>
        </ButtonSelect>
      </FormGroup>
      <FormGroup v-if="setting.displayTableType && !isTakeOut" :title="$t('reserve/seat')">
        <div v-if="invalidSeat && lastTouchKey" class="attention">
          {{ $t('invalid/seat', [$t(`reserve/${lastTouchKey}`).toLowerCase()]) }}
        </div>
        <ButtonSelect
          v-model.number="smoking"
          :items="{ ...$t('reserve/smokingTypes') }"
          :disabled-values="disabledSmokingTypes"
        >
          <template #default="{ value, label }">
            <noSmokingIcon v-if="!isJa && value === '1'" />
            <SmokingIcon v-else-if="!isJa && value === '2'" />
            <template v-else>{{ label }}</template>
          </template>
        </ButtonSelect>
      </FormGroup>
      <FormGroup
        :title="isTakeOut ? $t('reserve/receiptTime') : $t('reserve/time')"
        :style="`grid-gap: ${hasLunch && hasDinner ? 16 : 8}px 16px`"
      >
        <template v-if="hasLunch && hasDinner" #aside>
          <Selector
            v-model.number="zone"
            :items="{ 0: $t('lunch'), 1: $t('dinner') }"
            :disabled="disabledTimes.length > 0"
          />
        </template>
        <template v-if="availableTimes">
          <div v-if="invalidTime && lastTouchKey" class="attention">
            {{ $t('invalid/time', [$t(`reserve/${lastTouchKey}`).toLowerCase()]) }}
          </div>
          <ButtonSelect
            v-if="availableTimes.length"
            v-model="time"
            :items="availableTimes"
            :disabled-values="disabledTimes"
            scroll
          />
          <div v-else class="attention">{{ $t('reserve/time/notAvailable') }}</div>
        </template>
      </FormGroup>
      <FormGroup v-if="courses.allIds.length > 1 && !setting.useGnaviCourse" :title="$t(`reserve/${selectCourseType}`)">
        <CourseSelect
          v-for="(menu, index) in editCourses"
          :key="menu.index"
          :course="menu"
          :index="index"
          :is-take-out="isTakeOut"
          :type="selectCourseType"
          :selected-ids="isTakeOut ? editCourses.map(o => o.id) : []"
          :max="30"
        />
        <Button
          v-if="isTakeOut"
          class="add-course"
          :icon="AddIcon"
          :disabled="edit.detail.courses.length >= 10"
          @click="addCourse"
          >{{ $t('reserve/addMenuButton') }}</Button
        >
      </FormGroup>
      <FormGroup v-if="showMemberInfo" :title="$t('memberInfo')" required>
        <div class="member-info">
          <div>
            <b>{{ displayName }}</b>
            <div v-if="tel || mail" class="member-aside">
              <div>{{ tel }}</div>
              <div>{{ mail }}</div>
            </div>
          </div>
          <Button mini @click="showMemberInfo = false">{{ $t('change') }}</Button>
        </div>
      </FormGroup>
      <template v-else>
        <FormGroup :title="$t('reserve/name')" required>
          <div class="names">
            <template v-if="isJa">
              <TextInput
                v-model="lastName"
                autocomplete="family-name"
                :error="!!errors.lastName.length"
                :placeholder="$t('reserve/name/last')"
              />
              <TextInput
                v-model="firstName"
                autocomplete="given-name"
                :error="!!errors.firstName.length"
                :placeholder="$t('reserve/name/first')"
              />
              <TextInput
                v-model="lastNameKana"
                autocomplete="family-name"
                :error="!!errors.lastNameKana.length"
                :placeholder="$t('reserve/name/last/kana')"
              />
              <TextInput
                v-model="firstNameKana"
                autocomplete="given-name"
                :error="!!errors.firstNameKana.length"
                :placeholder="$t('reserve/name/first/kana')"
              />
            </template>
            <template v-else>
              <!-- 台帳側表示の都合上英語の場合は姓名を逆にして登録させる -->
              <TextInput
                v-model="lastName"
                autocomplete="given-name"
                :error="!!errors.firstName.length"
                :placeholder="$t('reserve/name/first')"
              />
              <TextInput
                v-model="firstName"
                autocomplete="family-name"
                :error="!!errors.lastName.length"
                :placeholder="$t('reserve/name/last')"
              />
            </template>
          </div>
          <Validation v-if="errors.name.length" :message="errors.name" />
        </FormGroup>
        <FormGroup :title="$t('reserve/tel')" required>
          <TextInput
            v-model="tel"
            type="tel"
            autocomplete="tel-local"
            placeholder="09012345678"
            :error="!!errors.tel.length"
          />
          <Validation v-if="errors.tel.length" :message="errors.tel" />
        </FormGroup>
        <FormGroup :title="$t('reserve/mail')" required>
          <TextInput v-model="mail" autocomplete="email" placeholder="sample@email.com" :error="!!errors.mail.length" />
          <Validation v-if="errors.mail.length" :message="errors.mail" />
        </FormGroup>
        <FormGroup v-if="!!setting.otherTxt.displayFlg" :title="$t('reserve/other')">
          <TextArea v-model="otherTxt" :error="!!errors.otherTxt.length" class="other" />
          <Validation v-if="errors.otherTxt" :message="errors.otherTxt" />
        </FormGroup>
      </template>
      <FormGroup v-if="setting.company.displayTerms" class="personal-permission">
        <CheckBox v-model="personalPermission">{{ $t('news/check', [$i18n.choice(setting.company.name)]) }}</CheckBox>
        <p>{{ $t('news/note') }}</p>
      </FormGroup>
    </div>
    <div class="actions">
      <p class="reserve_datetime">
        {{ `${$t('reserve/datetime')}：` }}
        <br v-if="$i18n.locale === 'en'" />
        <span v-if="date && number && time && !invalidTime">
          {{
            `${$d(new Date(date), 'short')}
            ${isTakeOut ? '' : `/ ${$tc('reserve/number/count/confirm', number)}`} / ${time}`
          }}
        </span>
        <span v-else> ーー/ーー{{ isTakeOut ? '' : '/ーー' }} </span>
      </p>
      <a v-if="showSct" class="sct" @click="openSctModal">{{ $t('reserve/sct') }}</a>
      <i18n class="tos_note" path="tos/note">
        <span>
          <template v-if="setting.company.displayTerms">
            <a :href="$i18n.choice(setting.company.termsUrl)" target="_blank">
              <span>{{ $t('tos/personal') }}</span>
              <ExternalIcon />
            </a>
            <span>・</span>
          </template>
          <a @click="openTosModal">{{ $t('tos') }}</a>
        </span>
      </i18n>
      <Button primary :disabled="hasError" @click="save">{{ $t('reserve') }}</Button>
    </div>
    <TosModal v-if="tosModal" @close="tosModal = false" />
    <SctModal v-if="sctModal" @close="sctModal = false" />
  </FormPage>
  <SimplePage
    v-else
    fixed-height
    :lang-selector="isSettingEn && !!tos.ja && !!tos.en"
    :loading="busy"
    :gap="24"
    :show-back="showBack"
    @back="onBack"
    @changeLang="event('daicho_terms_01')"
  >
    <div class="contents">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <p v-html="$t('tos/desc')" />
      <p ref="tos" class="tos" @scroll="checkScrollTos">{{ $i18n.choice(tos) || tos.en }}</p>
      <a v-if="showSct" class="sct" @click="openSctModal">{{ $t('reserve/sct') }}</a>
      <CheckBox v-model="confirmTos" class="confirmed" :disabled="!checkedTos" @change="onConfirmCheck">{{
        $t('confirmed')
      }}</CheckBox>
      <Button primary class="to-reception" :disabled="!confirmTos" @click="toReception">{{ $t('reserve') }}</Button>
    </div>
    <SctModal v-if="sctModal" @close="sctModal = false" />
  </SimplePage>
</template>

<script>
import { compact, debounce, throttle, times } from 'lodash'
import { mapState, mapGetters } from 'vuex'
import { event } from '@/utils/analytics'
import { models } from '@/utils/vue'
import FormPage from '@/components/templates/FormPage'
import SimplePage from '@/components/templates/SimplePage'
import TosModal from '@/components/organisms/TosModal'
import SctModal from '@/components/organisms/SctModal'
import ReserveDateInput from '@/components/organisms/ReserveDateInput'
import FormGroup from '@/components/molecules/FormGroup'
import ButtonSelect from '@/components/molecules/ButtonSelect'
import LoadingIndicator from '@/components/molecules/LoadingIndicator'
import GnaviCourseInfo from '@/components/molecules/GnaviCourseInfo'
import CourseSelect from '@/components/molecules/CourseSelect'
import Button from '@/components/atoms/Button'
import CheckBox from '@/components/atoms/CheckBox'
import Selector from '@/components/atoms/Selector'
import TextInput from '@/components/atoms/TextInput'
import TextArea from '@/components/atoms/TextArea'
import Validation from '@/components/atoms/Validation'
import SmokingIcon from '@/components/icons/smoking'
import noSmokingIcon from '@/components/icons/noSmoking'
import ExternalIcon from '@/components/icons/external'
import AddIcon from '@/components/icons/add'

export default {
  name: 'Reception',
  components: {
    FormPage,
    SimplePage,
    TosModal,
    SctModal,
    FormGroup,
    ButtonSelect,
    LoadingIndicator,
    GnaviCourseInfo,
    CourseSelect,
    Button,
    CheckBox,
    Selector,
    TextInput,
    TextArea,
    Validation,
    ReserveDateInput,
    SmokingIcon,
    noSmokingIcon,
    ExternalIcon,
  },
  data() {
    return {
      loaded: false,
      checkedTos: false,
      confirmTos: false,
      lastTouchKey: null,
      zone: 1,
      tosModal: false,
      sctModal: false,
      showMemberInfo: false,
      AddIcon,
    }
  },
  computed: {
    ...mapState({
      courses: state => state.courses,
      reception: state => state.reception,
      setting: state => state.reception.setting,
      confirmed: state => state.reception.confirmed,
      tos: state => state.reception.tos,
      sct: state => state.reception.sct,
      gnaviCourses: state => state.gnaviCourses,
      available: state => state.reception.available,
      edit: state => state.reservation.edit,
    }),
    ...mapGetters({
      hasLunch: 'reception/available/hasLunch',
      hasDinner: 'reception/available/hasDinner',
      selectedCourse: 'reservation/edit/selectedCourse',
    }),
    ...models(
      ['date', 'time', 'smoking', 'personalPermission', 'otherTxt'],
      ({ edit }, key) => edit.detail[key],
      (vm, key, value) => {
        if (!['personalPermission', 'otherTxt'].includes(key)) vm.lastTouchKey = key
        vm.$store.commit('reservation/edit/update', { [key]: value })
      },
    ),
    ...models(
      ['firstName', 'firstNameKana', 'lastName', 'lastNameKana', 'tel', 'mail'],
      ({ edit }, key) => edit.detail.customer[key],
      ({ $store }, key, value) => $store.commit('reservation/edit/update', { customer: { [key]: value } }),
    ),
    number: {
      get() {
        return this.edit.detail.number
      },
      set(count) {
        this.lastTouchKey = 'number'
        this.$store.commit('reservation/edit/update', { number: count })
        this.$store.commit('reservation/edit/updateCourse', { course: { ...this.editCourses[0], count }, index: 0 })
      },
    },
    busy() {
      return (
        this.reception.busy ||
        this.courses.busy ||
        this.tos.busy ||
        this.edit.busy ||
        this.available.busy ||
        this.sct.busy
      )
    },
    isJa() {
      return this.$i18n.locale === 'ja'
    },
    gnaviCourse() {
      if (!this.isSelectedCourse || !this.setting.useGnaviCourse) return null
      return this.gnaviCourses.byId[this.selectedCourse.id]
    },
    availableSeats() {
      if (this.isTakeOut) return [1]
      const { min, max } = this.available.seat
      return !this.date || !max ? [1, 2, 3, 4, 5, 6] : times(max - min + 1, i => min + i)
    },
    disabledSeats() {
      return !this.date ? this.availableSeats : []
    },
    disabledSmokingTypes() {
      if (!this.date || !this.number || this.invalidNumber) return ['0', '1', '2']
      const { smoking, noSmoking } = this.available
      return [!noSmoking && '1', !smoking && '2'].filter(v => v)
    },
    availableTimes() {
      if (!this.available.times || this.invalidNumber || this.invalidSeat)
        return ['18:00', '18:15', '18:30', '18:45', '19:00', '19:15']
      const times =
        !this.hasLunch || !this.hasDinner
          ? this.available.times
          : this.available.times.filter(({ type }) => type === this.zone)
      return times.map(({ time }) => time)
    },
    disabledTimes() {
      return !this.date || !this.number || this.invalidNumber || this.invalidSeat ? this.availableTimes : []
    },
    displayName() {
      const kana = this.lastNameKana && this.firstNameKana && `(${this.lastNameKana} ${this.firstNameKana})`
      return this.$i18n.locale === 'ja'
        ? `${this.lastName} ${this.firstName}${kana}`
        : `${this.lastName} ${this.firstName}`
    },
    // 電話番号半角数字判定
    checkTelInput() {
      return !!this.tel && !this.tel.match(/^[0-9]+$/) && this.$t('validate/tel')
    },
    // メールアドレス入力半角英数字チェック
    checkMailInput() {
      return !!this.mail && !this.mail.match(/^[!-~]+$/) && this.$t('validate/mail')
    },
    invalidNumber() {
      return this.number && !this.availableSeats.includes(this.number)
    },
    invalidSeat() {
      return this.smoking && this.disabledSmokingTypes.includes(`${this.smoking}`)
    },
    invalidTime() {
      return (
        this.time &&
        this.available.times &&
        this.available.times.length &&
        !this.available.times.some(({ time }) => time === this.time)
      )
    },
    errors() {
      const lastName = compact([
        this.validateLength('reserve/name/last', this.isJa ? this.lastName : this.firstName, 64),
      ])
      const firstName = compact([
        this.validateLength('reserve/name/first', this.isJa ? this.firstName : this.lastName, 64),
      ])
      const lastNameKana = compact([
        this.validateLength('reserve/name/last/kana', this.lastNameKana, 64),
        this.validateKana('reserve/name/last/kana', this.lastNameKana),
      ])
      const firstNameKana = compact([
        this.validateLength('reserve/name/first/kana', this.firstNameKana, 64),
        this.validateKana('reserve/name/first/kana', this.firstNameKana),
      ])
      return {
        // 名前入力エラーメッセージ
        lastName,
        firstName,
        lastNameKana,
        firstNameKana,
        name: [...lastName, ...firstName, ...lastNameKana, ...firstNameKana],
        // 電話番号入力エラーメッセージ
        tel: compact([this.validateLength('reserve/tel', this.tel, 64), this.checkTelInput]),
        // メールアドレス入力エラーメッセージ
        mail: compact([this.validateLength('reserve/mail', this.mail, 256), this.checkMailInput]),
        // その他テキストエラーメッセージ
        otherTxt: compact([this.validateLength('reserve/other', this.otherTxt, this.isJa ? 500 : 1000)]),
      }
    },
    hasError() {
      return (
        !this.date ||
        !this.number ||
        this.invalidNumber ||
        !this.time ||
        this.invalidTime ||
        (!this.lastName && !this.firstName && !this.lastNameKana && !this.firstNameKana) ||
        !this.tel ||
        !this.mail ||
        (this.isTakeOut && !this.editCourses.filter(o => o.id >= 0).length) ||
        Object.keys(this.errors).some(key => this.errors[key].length)
      )
    },
    showBack() {
      return this.setting.useGnaviCourse && this.isSelectedCourse
    },
    confirmedTos() {
      return !this.tos.ja && (!this.tos.en || !this.isSettingEn)
    },
    showNotes() {
      return !this.confirmed && !this.confirmedTos
    },
    isTakeOut() {
      return this.reception.setting.usageType === 2
    },
    editCourses() {
      const courses = this.edit.detail.courses
      return courses.length ? courses : [{ count: 1 }]
    },
    isSelectedCourse() {
      return !!Object.keys(this.selectedCourse).length
    },
    selectCourseType() {
      return this.isTakeOut ? 'menu' : 'course'
    },
    showSct() {
      return (!!this.sct.ja && this.isJa) || (!!this.sct.en && !this.isJa) || (!this.isSettingEn && !!this.sct.ja)
    },
    isSettingEn() {
      return this.setting.lang.en
    },
  },
  watch: {
    date() {
      this.fetchAvailable()
    },
    number() {
      this.fetchAvailable()
    },
    smoking() {
      this.fetchAvailable()
    },
    isJa(isJa) {
      if (!isJa) {
        this.lastNameKana = ''
        this.firstNameKana = ''
      }
      this.setDefaultOtherTxt()
    },
    showNotes(showNotes) {
      if (!showNotes) {
        this.$nextTick(() => {
          window.scroll(0, 0)
        })
      }
    },
  },
  async mounted() {
    const { dispatch } = this.$store
    const promises = [dispatch('reception/tos/get')]

    promises.push(dispatch('reception/sct/get'))

    if (!this.setting.useGnaviCourse) {
      promises.push(dispatch('courses/get'))
    } else if (this.isSelectedCourse) {
      promises.push(dispatch('gnaviCourses/get', this.selectedCourse.id))
    } else if (!this.gnaviCourses.allIds.length) {
      promises.push(dispatch('gnaviCourses/get'))
    }

    this.$store.commit('reservation/edit/start')

    await Promise.all(promises)

    // ぐるなびコース利用の場合にコースが選択されていなければコース一覧へ遷移
    if (this.setting.useGnaviCourse && this.gnaviCourses.allIds.length && !this.isSelectedCourse) {
      return this.$router.replace({ name: 'courses' })
    }

    if (this.date) this.fetchAvailable()
    if (this.isTakeOut) this.number = 1

    this.loaded = true
    this.$i18n.locale = this.isSettingEn && !this.tos.ja && !!this.tos.en ? 'en' : 'ja'
    this.personalPermission = this.setting.company.displayTerms
    this.showMemberInfo =
      !!this.edit.detail.customer.syncMemberId && this.firstName && this.lastName && this.tel && this.mail
    this.setDefaultOtherTxt()

    await this.$nextTick()
    if (this.showNotes) this.checkScrollTos()
  },
  methods: {
    event,
    onConfirmCheck(check) {
      if (check) event('daicho_terms_02')
    },
    toReception() {
      this.$store.commit('reception/confirmed', true)
    },
    fetchAvailable: debounce(async function() {
      if (!this.date) return
      await this.$store.dispatch('reception/available/get', {
        date: this.date,
        number: this.number,
        smoking: this.smoking,
        syncCourseId: this.gnaviCourse ? this.gnaviCourse.id : null,
      })
      if (this.invalidNumber) {
        this.$store.commit('reservation/edit/update', { smoking: 0, time: null })
      } else if (this.invalidSeat) {
        this.$store.commit('reservation/edit/update', { time: null })
      }
    }, 200),
    validateLength(key, value, max) {
      if (!value) return false
      const messageType = !this.isJa && key === 'reserve/other' ? 'validate/shortMaxlength' : 'validate/maxlength'
      return value.trim().length > max ? this.$t(messageType, { target: this.$t(key).toLowerCase(), max }) : null
    },
    validateKana(key, value) {
      return !!value && !value.match(/^[\u30a0-\u30ff\u3040-\u309f]+$/)
        ? `${this.$t(key)}はひらがなかカタカナで入力して下さい`
        : null
    },
    checkScrollTos: throttle(function() {
      const { clientHeight, scrollHeight, scrollTop } = this.$refs.tos
      if (clientHeight + scrollTop + 16 >= scrollHeight) this.checkedTos = true
    }, 200),
    openTosModal() {
      this.tosModal = true
      event('daicho_form_02')
    },
    onBack() {
      this.$router.push({ name: 'courses' })
    },
    async save() {
      const token = await this.$store.dispatch('reservation/edit/save', this.$i18n.locale)
      if (token) {
        this.$router.push({ name: 'reservation', params: { token } })
      } else {
        document.documentElement.scrollTop = 0
      }
    },
    onlySeat(kind) {
      return !!kind && kind === 6
    },
    setDefaultOtherTxt() {
      this.$store.commit('reservation/edit/update', {
        otherTxt: this.setting.otherTxt.displayFlg ? this.$i18n.choice(this.setting.otherTxt) + '\n' : null,
      })
    },
    addCourse() {
      this.$store.commit('reservation/edit/update', {
        courses: [...this.editCourses, { count: 1 }],
      })
    },
    openSctModal() {
      this.sctModal = true
    },
  },
}
</script>

<style lang="postcss" scoped>
.contents {
  align-content: flex-start;
  box-sizing: border-box;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  justify-items: center;
  overflow: hidden;

  @media screen and (min-width: 768px) {
    background: #fff;
    flex: 0 1 auto;
    justify-items: center;
    padding: 40px 60px 40px;
  }
}

.tos {
  background: #fff;
  border: 1px solid #aaa;
  box-sizing: border-box;
  flex: 1 1 auto;
  height: 280px;
  margin: 16px auto 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  padding: 16px 8px;
  white-space: pre-wrap;
  width: 100%;

  @media screen and (min-width: 768px) {
    max-width: 740px;
    padding: 16px;
  }
}

.sct {
  cursor: pointer;
  display: flex;
  font-size: 12px;
  justify-content: center;
  margin: 24px 0 8px;
  text-decoration: underline;

  @media screen and (min-width: 768px) {
    margin-bottom: 18px;
  }
}

.confirmed {
  flex-shrink: 0;
  font-size: 18px;
  font-weight: 600;
  justify-content: center;
  margin: 16px auto;
}

.to-reception {
  flex-shrink: 0;
  margin: 0 auto;
}

.error {
  background: var(--very-light-gray-2);
  color: var(--bright-red-2);
  font-weight: 600;
  margin-bottom: 16px;
  padding: 16px;
  text-align: center;
  white-space: pre-line;

  @media screen and (min-width: 768px) {
    margin: 0 48px 16px;
  }
}

.form {
  background: #fff;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  padding-bottom: 16px;

  @media screen and (min-width: 768px) {
    padding: 40px 48px 56px;
  }
}

.attention {
  color: var(--bright-red-2);
  font-weight: 600;
  text-align: center;
}

.add-course {
  font-size: 14px;
  height: 48px;
  margin-top: 16px;
  padding-left: 6px;
  width: 140px;

  & >>> .content {
    padding-left: 6px;
  }
}

.member-info {
  align-items: center;
  display: grid;
  grid-template-columns: 1fr auto;

  & .button {
    font-size: 14px;
    height: 38px;
    width: auto;
  }

  @media screen and (max-width: 767px) {
    & .button {
      border-width: 0;
      color: var(--brand-color);
      font-weight: 300;
      height: auto;
      padding: 0;
      width: auto;
    }
  }

  @media screen and (min-width: 768px) {
    background: var(--mostly-white-2);
    padding: 24px;
  }
}

.member-aside {
  margin-top: 8px;
}

.names {
  display: grid;
  grid-gap: 8px;
  grid-template-columns: 1fr 1fr;

  @media screen and (min-width: 768px) {
    grid-gap: 8px 16px;
  }
}

.other {
  height: 144px;
  resize: none;
  @media screen and (min-width: 768px) {
    height: 120px;
  }
}

.personal-permission {
  color: var(--very-dark-gray-1);

  & p {
    color: var(--text-color);
    font-size: 12px;
    margin-top: 4px;
    white-space: pre-line;

    @media screen and (min-width: 768px) {
      margin-top: 12px;
    }
  }
}

.actions {
  align-items: center;
  background: var(--very-pale-yellow);
  display: flex;
  flex-direction: column;
  padding: 24px 16px;

  & .reserve_datetime {
    font-weight: 600;
    margin-bottom: 16px;
    text-align: center;
  }

  & .sct {
    margin-top: 8px;
  }

  & .tos_note {
    font-size: 12px;
    margin-bottom: 16px;
    padding-bottom: 12px;
    text-align: center;
    word-break: normal;

    & a {
      cursor: pointer;
      text-decoration: underline;
    }

    & svg {
      height: 16px;
      vertical-align: sub;
      width: 16px;
    }
  }
}

.button {
  border-color: var(--form-border-color);

  & svg {
    height: 32px;
    width: 32px;
  }
}

hr {
  border: 1px solid var(--light-gray);
  margin: 24px 0px 40px 0px;

  @media screen and (max-width: 768px) {
    display: none;
  }
}
</style>
